import { formatDollarsFromCents } from "lib";

export enum RoutingSide {
  Individual = "individual",
  Cash = "cash",
}

export function findSaleLotsForBuyer(
  saleLotsById: Record<string, SaleLot>,
  livestockSaleId: number,
  buyerId: string,
  cashBuyerBusinessId: string | null,
): SaleLot[] {
  return Object.values(saleLotsById).filter(saleLot => {
    if (saleLot.livestocksale_id !== livestockSaleId) return false;

    const resolvedBusinessId = saleLot.invoiceToBusinessId || saleLot.buyer_id;
    if (resolvedBusinessId === buyerId) return true;

    // Already routed to the cash buyer by a previous confirm - still this
    // buyer's lot for the purpose of toggling it back to individual.
    return (
      saleLot.buyer_id === buyerId && resolvedBusinessId === cashBuyerBusinessId
    );
  });
}

export interface BuyerSaleLotSummary {
  lotCount: number;
  totalPaidCents: number;
}

/**
 * Mirrors the "Paid" figure on the Clearing Sale buyer card
 * (ClearingSaleCards/SaleLotSummary.js) - the buyer's actual payments, not
 * their lots' sale value, since that's what decides Cash Account routing.
 */
export function summarizeBuyerSaleLots(
  saleLots: SaleLot[],
  buyerId: string,
  paymentsById: Record<string, Payment>,
): BuyerSaleLotSummary {
  const totalPaidCents = Object.values(paymentsById)
    .filter(payment => payment.fromBusinessId === buyerId)
    .reduce((sum, payment) => sum + (payment.totalAmountCents || 0), 0);

  return { lotCount: saleLots.length, totalPaidCents };
}

export interface ConfirmPayload {
  buyerIdsToRouteToCash: string[];
  buyerIdsToRouteToIndividual: string[];
}

export function buildConfirmPayload(
  suggestions: AccountingDayRoutingSuggestion[],
  routingByBuyerId: Record<string, boolean>,
  saleLotsById: Record<string, SaleLot>,
  livestockSaleId: number,
  cashBuyerBusinessId: string | null,
): ConfirmPayload {
  const buyerIdsToRouteToCash: string[] = [];
  const buyerIdsToRouteToIndividual: string[] = [];

  suggestions.forEach(suggestion => {
    const isRoutingToCash = Boolean(routingByBuyerId[suggestion.id]);
    const targetInvoiceToBusinessId = isRoutingToCash
      ? cashBuyerBusinessId
      : null;

    const pendingSaleLots = findSaleLotsForBuyer(
      saleLotsById,
      livestockSaleId,
      suggestion.id,
      cashBuyerBusinessId,
    ).filter(
      saleLot =>
        (saleLot.invoiceToBusinessId || null) !== targetInvoiceToBusinessId,
    );

    if (pendingSaleLots.length === 0) return;

    if (isRoutingToCash) {
      buyerIdsToRouteToCash.push(suggestion.id);
    } else {
      buyerIdsToRouteToIndividual.push(suggestion.id);
    }
  });

  return { buyerIdsToRouteToCash, buyerIdsToRouteToIndividual };
}

/**
 * Where the backend's suggestion would put this buyer, absent any manual
 * override from the user.
 */
export function getSuggestedSide(
  suggestion: AccountingDayRoutingSuggestion,
): RoutingSide {
  if (suggestion.suggestCashAccount) {
    return RoutingSide.Cash;
  }
  return RoutingSide.Individual;
}

/**
 * Where a buyer currently sits: an explicit user override always wins; failing
 * that, a buyer already routed to Cash Account stays there - that's their
 * actual current state, not a suggestion to review. Failing that, the
 * algorithmic suggestion applies only once the user has accepted suggestions -
 * before that, every other buyer defaults to Individual Invoicing.
 */
export function resolveBuyerDestination(
  suggestion: AccountingDayRoutingSuggestion,
  explicitDestinationByBuyerId: Record<string, RoutingSide>,
  suggestionsAccepted: boolean,
): RoutingSide {
  const explicitDestination = explicitDestinationByBuyerId[suggestion.id];
  if (explicitDestination) {
    return explicitDestination;
  }

  if (suggestion.alreadyRoutedToCash) {
    return RoutingSide.Cash;
  }

  if (suggestionsAccepted) {
    return getSuggestedSide(suggestion);
  }

  return RoutingSide.Individual;
}

export function filterAndSortSuggestions(
  suggestions: AccountingDayRoutingSuggestion[],
  side: RoutingSide,
  explicitDestinationByBuyerId: Record<string, RoutingSide>,
  suggestionsAccepted: boolean,
  searchText: string,
  showFlaggedOnly: boolean,
  buyerNameById: Record<string, string>,
  saleLotSummaryByBuyerId: Record<string, BuyerSaleLotSummary>,
): AccountingDayRoutingSuggestion[] {
  const normalizedSearchText = searchText.trim().toLowerCase();

  const matchingSuggestions = suggestions.filter(suggestion => {
    const destination = resolveBuyerDestination(
      suggestion,
      explicitDestinationByBuyerId,
      suggestionsAccepted,
    );
    if (destination !== side) {
      return false;
    }

    if (normalizedSearchText) {
      const buyerName = (buyerNameById[suggestion.id] || "").toLowerCase();
      if (!buyerName.includes(normalizedSearchText)) {
        return false;
      }
    }

    if (
      side === RoutingSide.Individual &&
      showFlaggedOnly &&
      !suggestion.suggestCashAccount
    ) {
      return false;
    }

    return true;
  });

  return matchingSuggestions.sort((a, b) => {
    if (
      side === RoutingSide.Individual &&
      a.suggestCashAccount !== b.suggestCashAccount
    ) {
      return a.suggestCashAccount ? -1 : 1;
    }

    const totalPaidCentsA = saleLotSummaryByBuyerId[a.id]?.totalPaidCents || 0;
    const totalPaidCentsB = saleLotSummaryByBuyerId[b.id]?.totalPaidCents || 0;
    return totalPaidCentsB - totalPaidCentsA;
  });
}

/**
 * How many buyers "Accept all suggestions" would actually move to Cash
 * Account: suggested for it, not already there, and without an explicit
 * manual decision already made for them (in either direction) - the button
 * never touches those, so they don't count as pending regardless of whether
 * they happen to agree or disagree with the suggestion.
 */
export function countPendingSuggestions(
  suggestions: AccountingDayRoutingSuggestion[],
  explicitDestinationByBuyerId: Record<string, RoutingSide>,
  suggestionsAccepted: boolean,
): number {
  if (suggestionsAccepted) {
    return 0;
  }

  const pendingSuggestions = suggestions.filter(
    suggestion =>
      suggestion.suggestCashAccount &&
      !suggestion.alreadyRoutedToCash &&
      !explicitDestinationByBuyerId[suggestion.id],
  );

  return pendingSuggestions.length;
}

export function countBuyersNeedingReview(
  suggestions: AccountingDayRoutingSuggestion[],
  explicitDestinationByBuyerId: Record<string, RoutingSide>,
  suggestionsAccepted: boolean,
): number {
  const buyersNeedingReview = suggestions.filter(suggestion => {
    if (!suggestion.suggestCashAccount) {
      return false;
    }

    const destination = resolveBuyerDestination(
      suggestion,
      explicitDestinationByBuyerId,
      suggestionsAccepted,
    );
    return destination === RoutingSide.Individual;
  });

  return buyersNeedingReview.length;
}

export function buildColumnSummaryText(
  rows: AccountingDayRoutingSuggestion[],
  selectedCount: number,
  saleLotSummaryByBuyerId: Record<string, BuyerSaleLotSummary>,
): string {
  if (selectedCount > 0) {
    return `${selectedCount} of ${rows.length} selected`;
  }

  const totalCents = rows.reduce((sum, row) => {
    return sum + (saleLotSummaryByBuyerId[row.id]?.totalPaidCents || 0);
  }, 0);

  const buyerLabel = rows.length === 1 ? "buyer" : "buyers";
  return `${rows.length} ${buyerLabel} · ${formatDollarsFromCents(totalCents, {
    format: true,
  })} paid`;
}
